import React from "react";
import Select from "react-select";
import navigationImg from "@/assets/img/navigation.PNG";
import footerImg from "@/assets/img/Footer.PNG";
import ContentArrayLinks from "../UpdatePage/ContentArrayLinks";
import { resolveContentDeclarations } from "../UpdatePage/updatePageHelpers";
import VariableField, {
  bindFieldProps,
  hasFieldFor,
} from "../../variableFields/VariableField";
import {
  Row,
  Col,
  Card,
  Button,
  UncontrolledTooltip,
  ListGroup,
  ListGroupItem,
  CardImg,
  ListGroupItemHeading,
  ListGroupItemText,
  CardHeader,
  CardBody,
  FormGroup,
  Label,
  Input,
  type InputProps,
} from "reactstrap";

const imgForType = (type) => {
  if (type === "navigation") return navigationImg;
  if (type === "footer") return footerImg;
  return null;
};

// Boolean options for select
const boolOptions = [
  { label: "False", value: false },
  { label: "True", value: true },
];

// S4 registry blocks (SCRUM-853): module-level statics with ownsChrome so the
// dispatcher keeps this screen's legacy FormGroup + "font-weight-bold" label
// (no md=3/md=9 row); the per-column handlers travel via fieldProps.

// String, and the pinned silent fallback for every type without an editor
// here: plain text input reporting the raw change event on the column's
// onVariableChange channel (first keystroke re-types the value to a string).
type RequiredPagesTextFieldProps = {
  settingVar: { variable?: string; value?: string | number };
  onChange: React.ChangeEventHandler<HTMLInputElement>;
  inputType?: InputProps["type"];
};

const RequiredPagesTextField = ({ settingVar, onChange, inputType = "text" }: RequiredPagesTextFieldProps) => (
  <FormGroup>
    <Label className="font-weight-bold">{settingVar.variable}</Label>
    <Input
      type={inputType}
      name={settingVar.variable}
      value={settingVar.value || ""}
      onChange={onChange}
      placeholder={`Enter ${settingVar.variable}`}
    />
  </FormGroup>
);
RequiredPagesTextField.ownsChrome = true;

// Number keeps `value || ""` (a stored 0 renders empty) and the pinned
// number-stored-as-string channel through the shared text block.
const RequiredPagesNumberField = bindFieldProps(RequiredPagesTextField, {
  inputType: "number",
});

/** A row of the navigation / footer page content, as the host passes it. */
type ContentVar = { variable?: string; variabletype?: string; value?: any }; // any: the stored value differs per variabletype (string, number, boolean, { name, link }, link rows); the Link block reads .name / .link, the Boolean block compares

type RequiredPagesBooleanFieldProps = {
  settingVar: ContentVar;
  onChange: (option: { label: string; value: boolean } | null) => void;
};

const RequiredPagesBooleanField = ({ settingVar, onChange }: RequiredPagesBooleanFieldProps) => (
  <FormGroup>
    <Label className="font-weight-bold">{settingVar.variable}</Label>
    <Select
      name={settingVar.variable}
      options={boolOptions}
      value={boolOptions.find((o) => o.value === settingVar.value)}
      onChange={onChange}
    />
  </FormGroup>
);
RequiredPagesBooleanField.ownsChrome = true;

// S4-only "Link Name"/"Link URL" placeholders; reports (event, varName)
// through the column's onLinkChange.
type RequiredPagesLinkFieldProps = {
  settingVar: ContentVar;
  onLinkChange: (e: React.ChangeEvent<HTMLInputElement>, variableName: string | undefined) => void;
};

const RequiredPagesLinkField = ({ settingVar, onLinkChange }: RequiredPagesLinkFieldProps) => (
  <FormGroup>
    <Label className="font-weight-bold">{settingVar.variable}</Label>
    <Row>
      <Col md="6">
        <Input
          type="text"
          name="name"
          value={settingVar.value?.name || ""}
          onChange={(e) => onLinkChange(e, settingVar.variable)}
          placeholder="Link Name"
          className="mb-2"
        />
      </Col>
      <Col md="6">
        <Input
          type="text"
          name="link"
          value={settingVar.value?.link || ""}
          onChange={(e) => onLinkChange(e, settingVar.variable)}
          placeholder="Link URL"
        />
      </Col>
    </Row>
  </FormGroup>
);
RequiredPagesLinkField.ownsChrome = true;

// RAW ContentArrayLinks embed (not ArrayLinksEditor): the reported rows keep
// the editor-internal `index` key (S4-only persisted shape) and the delete
// guard derives per-variable from variabletype (ContentRequired pins the
// last row).
type RequiredPagesArrayLinksFieldProps = {
  settingVar: ContentVar;
  onArrayAdd: (...args: any[]) => void; // any: forwarded unchanged to ContentArrayLinks add; the host passes its arrayChange (linksArray, varName)
  onArrayDelete: (...args: any[]) => void; // any: forwarded unchanged to ContentArrayLinks delete; the host passes its arrayChange (linksArray, varName)
  onArrayLinkChange: (...args: any[]) => void; // any: forwarded unchanged to ContentArrayLinks handleLinkArray; the host passes its arrayChange (linksArray, varName)
};

const RequiredPagesArrayLinksField = ({ settingVar, onArrayAdd, onArrayDelete, onArrayLinkChange }: RequiredPagesArrayLinksFieldProps) => (
  <FormGroup>
    <Label className="font-weight-bold">{settingVar.variable} (Links Array)</Label>
    <ContentArrayLinks
      variableName={settingVar}
      add={onArrayAdd}
      delete={onArrayDelete}
      handleLinkArray={onArrayLinkChange}
      isRequired={settingVar.variabletype === "ContentRequired"}
    />
  </FormGroup>
);
RequiredPagesArrayLinksField.ownsChrome = true;

// Static module-level override map (never closes over per-render values —
// SCRUM-643 hard rule). Every registry-known type without an S4 editor is
// pinned to the fallback text block so no type silently gains a registry
// editor on this screen; types the registry does not know (faq, tips, ...)
// miss hasFieldFor and take the same fallback in the host render.
const REQUIRED_PAGES_OVERRIDES = {
  String: RequiredPagesTextField,
  Number: RequiredPagesNumberField,
  Boolean: RequiredPagesBooleanField,
  Link: RequiredPagesLinkField,
  "Array:links": RequiredPagesArrayLinksField,
  Array: RequiredPagesTextField,
  Product: RequiredPagesTextField,
  // Category / Subcategory / Ad / Blog / Json / Image left the registry with
  // SCRUM-2207, so they miss hasFieldFor and take the same text fallback.
  service: RequiredPagesTextField,
};

/**
 * The host (RequiredPagesContainer.tsx) reads pages and templates from slices
 * that type them `any` and keeps its own types module-local; this view renders
 * and forwards them, so they keep that looseness here (`any`) for now.
 */
type Handler = (...args: any[]) => void; // any: 20 forwarded host handlers with differing argument lists (option, change event plus action, link rows, page); the view never calls them itself

const MODERATOR_ROLES: ReadonlyArray<string | null | undefined> = [
  "ADMINISTRATOR",
  "CONTENTDIRECTOR",
  "CONTENTCOORDINATOR",
];

export type RequiredPagesViewProps = {
  pages: any[]; // any: CurrentSite.required_pages from sitesSlice.ts, which is still JavaScript and untyped
  role?: string | null;
  permission?: { publish?: boolean; edit?: boolean } | null;
  onEditRequired: Handler;
  onDeletePage: Handler;
  hasNavigation: boolean;
  hasFooter: boolean;
  navigationTemplates?: any[]; // any: filtered templates_by_design_site, typed any[] in templateSlice.ts:24
  footerTemplates?: any[]; // any: filtered templates_by_design_site, typed any[] in templateSlice.ts:24
  selectedNavTemplate: any; // any: one templates_by_design_site entry (any[] in templateSlice.ts:24); the host's RequiredPageTemplate is module-local
  selectedFooterTemplate: any; // any: one templates_by_design_site entry (any[] in templateSlice.ts:24); the host's RequiredPageTemplate is module-local
  navPageContent: any[]; // any: the host's PageContentEntry[] rows; resolveContentDeclarations adds the template declaration before VariableField renders them
  footerPageContent: any[]; // any: the host's PageContentEntry[] rows; resolveContentDeclarations adds the template declaration before VariableField renders them
  onNavTemplateChange: Handler;
  onFooterTemplateChange: Handler;
  onNavVariableChange: Handler;
  onFooterVariableChange: Handler;
  onNavLinkChange: Handler;
  onFooterLinkChange: Handler;
  onNavArrayAdd: Handler;
  onNavArrayDelete: Handler;
  onNavArrayLinkChange: Handler;
  onFooterArrayAdd: Handler;
  onFooterArrayDelete: Handler;
  onFooterArrayLinkChange: Handler;
  onCreateNavigation: Handler;
  onCreateFooter: Handler;
  isCreating: boolean;
};

const RequiredPagesView = ({
  pages,
  role,
  permission,
  onEditRequired,
  onDeletePage,
  // Creation props
  hasNavigation,
  hasFooter,
  navigationTemplates,
  footerTemplates,
  selectedNavTemplate,
  selectedFooterTemplate,
  navPageContent,
  footerPageContent,
  onNavTemplateChange,
  onFooterTemplateChange,
  onNavVariableChange,
  onFooterVariableChange,
  onNavLinkChange,
  onFooterLinkChange,
  // Array type handlers
  onNavArrayAdd,
  onNavArrayDelete,
  onNavArrayLinkChange,
  onFooterArrayAdd,
  onFooterArrayDelete,
  onFooterArrayLinkChange,
  onCreateNavigation,
  onCreateFooter,
  isCreating,
}: RequiredPagesViewProps) => {
  const canModerate = MODERATOR_ROLES.includes(role);
  const canEditOrPublish = permission?.publish || permission?.edit;

  // Format templates for react-select
  const navTemplateOptions = (navigationTemplates || []).map((t) => ({
    label: t.name,
    value: t,
  }));

  const footerTemplateOptions = (footerTemplates || []).map((t) => ({
    label: t.name,
    value: t,
  }));

  const showCreateSection = !hasNavigation || !hasFooter;

  // SCRUM-1514: the container's content is the SAVE state and carries the
  // variable and its value only, so which column a variable belongs to — and
  // which input it renders as — is derived here from the picked template. Same
  // split as before; the two columns are what would go empty if this derivation
  // were dropped along with the stored tag.
  const navContent = resolveContentDeclarations(navPageContent, selectedNavTemplate);
  const footerContent = resolveContentDeclarations(footerPageContent, selectedFooterTemplate);

  // Filter required variables
  const navRequiredVars = navContent.filter((v) => v.variabletype === "ContentRequired");
  const navOptionalVars = navContent.filter((v) => v.variabletype === "ContentOptional");
  const footerRequiredVars = footerContent.filter((v) => v.variabletype === "ContentRequired");
  const footerOptionalVars = footerContent.filter((v) => v.variabletype === "ContentOptional");

  const navFieldProps = {
    onLinkChange: onNavLinkChange,
    onArrayAdd: onNavArrayAdd,
    onArrayDelete: onNavArrayDelete,
    onArrayLinkChange: onNavArrayLinkChange,
  };
  const footerFieldProps = {
    onLinkChange: onFooterLinkChange,
    onArrayAdd: onFooterArrayAdd,
    onArrayDelete: onFooterArrayDelete,
    onArrayLinkChange: onFooterArrayLinkChange,
  };

  const renderVariable = (v, onVariableChange, fieldProps) =>
    hasFieldFor(v, REQUIRED_PAGES_OVERRIDES) ? (
      <VariableField
        key={v.variable}
        settingVar={v}
        options={undefined}
        onChange={onVariableChange}
        overrides={REQUIRED_PAGES_OVERRIDES}
        fieldProps={fieldProps}
      />
    ) : (
      <RequiredPagesTextField key={v.variable} settingVar={v} onChange={onVariableChange} />
    );

  return (
    <div className="container-fluid">
      {/* Creation Section - Show when navigation or footer is missing */}
      {showCreateSection && canModerate && (
        <Row className="mb-4">
          {/* Create Navigation */}
          {!hasNavigation && (
            <Col md="6">
              <Card className="shadow">
                <CardHeader className="bg-primary text-white">
                  <h4 className="mb-0">Create Navigation</h4>
                </CardHeader>
                <CardBody>
                  <FormGroup>
                    <Label>Select Header Template</Label>
                    <Select
                      options={navTemplateOptions}
                      value={navTemplateOptions.find((o) => o.value?._id === selectedNavTemplate?._id) || null}
                      onChange={onNavTemplateChange}
                      placeholder="Choose a header template..."
                      isClearable
                      isSearchable
                    />
                  </FormGroup>

                  {/* Show variables when template is selected */}
                  {selectedNavTemplate && navPageContent?.length > 0 && (
                    <>
                      {navRequiredVars.length > 0 && (
                        <>
                          <h5 className="text-primary mt-3">Required Variables</h5>
                          <hr />
                          {navRequiredVars.map((v) => renderVariable(v, onNavVariableChange, navFieldProps))}
                        </>
                      )}
                      {navOptionalVars.length > 0 && (
                        <>
                          <h5 className="text-secondary mt-3">Optional Variables</h5>
                          <hr />
                          {navOptionalVars.map((v) => renderVariable(v, onNavVariableChange, navFieldProps))}
                        </>
                      )}
                    </>
                  )}

                  <Button
                    color="success"
                    onClick={onCreateNavigation}
                    disabled={!selectedNavTemplate || isCreating}
                    block
                    className="mt-3"
                  >
                    {isCreating ? "Creating..." : "Create Navigation"}
                  </Button>
                </CardBody>
              </Card>
            </Col>
          )}

          {/* Create Footer */}
          {!hasFooter && (
            <Col md="6">
              <Card className="shadow">
                <CardHeader className="bg-info text-white">
                  <h4 className="mb-0">Create Footer</h4>
                </CardHeader>
                <CardBody>
                  <FormGroup>
                    <Label>Select Footer Template</Label>
                    <Select
                      options={footerTemplateOptions}
                      value={footerTemplateOptions.find((o) => o.value?._id === selectedFooterTemplate?._id) || null}
                      onChange={onFooterTemplateChange}
                      placeholder="Choose a footer template..."
                      isClearable
                      isSearchable
                    />
                  </FormGroup>

                  {/* Show variables when template is selected */}
                  {selectedFooterTemplate && footerPageContent?.length > 0 && (
                    <>
                      {footerRequiredVars.length > 0 && (
                        <>
                          <h5 className="text-primary mt-3">Required Variables</h5>
                          <hr />
                          {footerRequiredVars.map((v) => renderVariable(v, onFooterVariableChange, footerFieldProps))}
                        </>
                      )}
                      {footerOptionalVars.length > 0 && (
                        <>
                          <h5 className="text-secondary mt-3">Optional Variables</h5>
                          <hr />
                          {footerOptionalVars.map((v) => renderVariable(v, onFooterVariableChange, footerFieldProps))}
                        </>
                      )}
                    </>
                  )}

                  <Button
                    color="success"
                    onClick={onCreateFooter}
                    disabled={!selectedFooterTemplate || isCreating}
                    block
                    className="mt-3"
                  >
                    {isCreating ? "Creating..." : "Create Footer"}
                  </Button>
                </CardBody>
              </Card>
            </Col>
          )}
        </Row>
      )}

      {/* Existing Pages Display */}
      <Row>
        {(pages || []).map((page) => {
          const imgSrc = imgForType(page?.type);
          const templateName = page?.page_template?.name;
          const templateType = page?.page_template?.type;

          return (
            <Col className="col-auto" style={{ float: "none", margin: "0 auto" }} key={page._id}>
              <Card className="card-lift--hover shadow border-0">
                {imgSrc && <CardImg alt="..." src={imgSrc} top />}
                <ListGroup flush>
                  <ListGroupItem>
                    <Row>
                      <Col>
                        <ListGroupItemHeading>Template Name</ListGroupItemHeading>
                        {templateName && <ListGroupItemText>{templateName}</ListGroupItemText>}
                      </Col>
                      <Col>
                        <ListGroupItemHeading>Template Type</ListGroupItemHeading>
                        {templateType && <ListGroupItemText>{templateType}</ListGroupItemText>}
                      </Col>
                    </Row>
                  </ListGroupItem>
                  <ListGroupItem className="text-center">
                    <Row className="align-items-center">
                      <div className="col-6">
                        <Button
                          className="btn btn-lg btn-primary mr-4"
                          id={`btnContentVariableNav_${page._id}`}
                          color="default"
                          size="lg"
                          onClick={() => onEditRequired(page._id)}
                        >
                          <i className="ni ni-settings-gear-65" />
                        </Button>
                        <UncontrolledTooltip delay={0} placement="top" target={`btnContentVariableNav_${page._id}`}>
                          edit Page Content Variables Values
                        </UncontrolledTooltip>
                      </div>
                      {canModerate && canEditOrPublish && (
                        <Col md="6">
                          <Button
                            className="mr-4"
                            color="danger"
                            size="lg"
                            data-placement="top"
                            id={`buttondeleteNav_${page._id}`}
                            onClick={(e) => onDeletePage(page, e)}
                          >
                            <i className="fas fa-trash" />
                          </Button>
                          <UncontrolledTooltip delay={0} placement="top" target={`buttondeleteNav_${page._id}`}>
                            Delete
                          </UncontrolledTooltip>
                        </Col>
                      )}
                    </Row>
                  </ListGroupItem>
                </ListGroup>
              </Card>
            </Col>
          );
        })}
      </Row>
    </div>
  );
};

export default RequiredPagesView;
